import { ANALYTICS_EVENTS_PER_REQUEST_MAX, analyticsEventsResponseSchema, customerAnalyticsPreferenceSchema, featureFlagExposureResponseSchema, featureFlagsResponseSchema, isValidClientAnalyticsEvent, } from "@lafayette/schemas";
import { createMemoryTransport, } from "./memory-transport";
/** The queue entry of one event. `occurredAt` is an ISO time; a date that is not a date is left out, and the event then has the server time. */
function queuedEvent(event, properties, occurredAt) {
    return occurredAt === undefined || Number.isNaN(occurredAt.getTime())
        ? { event, properties }
        : { event, properties, occurredAt: occurredAt.toISOString() };
}
export function createAnalyticsClient(options) {
    const { audience, onError } = options;
    const maxQueue = options.maxQueue ?? 100;
    const send = createMemoryTransport({ ...options, maxNetworkRetries: 0 });
    const prefix = `/${audience}`;
    let active = options.enabled === true;
    let queue = [];
    let flushing = Promise.resolve();
    const exposed = new Set();
    // The signed decisions of the last `featureFlags` answer: what the app was shown, to hand back for an exposure.
    const decisions = new Map();
    const report = (error) => {
        const status = error?.status;
        onError?.(typeof status === "number" ? `HTTP_${status}` : "NETWORK");
    };
    const flush = () => {
        flushing = flushing.then(async () => {
            while (active && queue.length > 0) {
                const taken = queue.slice(0, ANALYTICS_EVENTS_PER_REQUEST_MAX);
                queue = queue.slice(taken.length);
                // Checked again at the door: nothing that fails the contract is sent.
                const events = taken.filter((entry) => isValidClientAnalyticsEvent(entry.event, entry.properties ?? {}));
                if (events.length !== taken.length)
                    onError?.("INVALID_EVENT");
                if (events.length === 0)
                    continue;
                try {
                    const answer = await send({
                        method: "POST",
                        path: `${prefix}/analytics/events`,
                        body: { events },
                        schema: analyticsEventsResponseSchema,
                    });
                    if (answer.status === "disabled") {
                        active = false;
                        queue = [];
                    }
                }
                catch (error) {
                    report(error);
                }
            }
        });
        return flushing;
    };
    return {
        get active() {
            return active;
        },
        discard() {
            queue = [];
        },
        capture(event, properties = {}, captureOptions) {
            if (!active)
                return;
            if (!isValidClientAnalyticsEvent(event, properties)) {
                onError?.("INVALID_EVENT");
                return;
            }
            if (queue.length >= maxQueue) {
                queue.shift();
                onError?.("QUEUE_FULL");
            }
            queue.push(queuedEvent(event, properties, captureOptions?.occurredAt));
            if (queue.length >= ANALYTICS_EVENTS_PER_REQUEST_MAX)
                void flush();
        },
        flush,
        async featureFlags(defaults) {
            if (!active)
                return { ...defaults };
            try {
                const answer = await send({
                    method: "GET",
                    path: `${prefix}/feature-flags`,
                    schema: featureFlagsResponseSchema,
                });
                const flags = { ...defaults };
                decisions.clear();
                for (const key of Object.keys(defaults)) {
                    const value = Object.hasOwn(answer.flags, key) ? answer.flags[key] : undefined;
                    if (typeof value !== "string")
                        continue;
                    flags[key] = value;
                    const decision = Object.hasOwn(answer.decisions, key) ? answer.decisions[key] : undefined;
                    if (typeof decision === "string")
                        decisions.set(key, decision);
                }
                return flags;
            }
            catch (error) {
                report(error);
                return { ...defaults };
            }
        },
        async exposure(flag) {
            const decision = decisions.get(flag);
            if (!active || decision === undefined || exposed.has(flag))
                return;
            exposed.add(flag);
            try {
                await send({
                    method: "POST",
                    path: `${prefix}/feature-flags/exposure`,
                    body: { flag, decision },
                    schema: featureFlagExposureResponseSchema,
                });
            }
            catch (error) {
                // A failed report may be tried again at the next screen: forget it.
                exposed.delete(flag);
                report(error);
            }
        },
    };
}
/** A client for the tests of an app: it records the calls and makes no request. */
export function createRecordingAnalyticsClient(flags = {}) {
    const events = [];
    const exposures = [];
    return {
        events,
        exposures,
        active: true,
        capture: (event, properties = {}, captureOptions) => {
            events.push(queuedEvent(event, properties, captureOptions?.occurredAt));
        },
        discard: () => {
            events.length = 0;
        },
        flush: () => Promise.resolve(),
        featureFlags: (defaults) => Promise.resolve({ ...defaults, ...flags }),
        exposure: (flag) => {
            if (!exposures.includes(flag))
                exposures.push(flag);
            return Promise.resolve();
        },
    };
}
export function createAnalyticsPreferenceApi(options) {
    const send = createMemoryTransport(options);
    return {
        getAnalyticsPreference: (callOptions) => send({
            method: "GET",
            path: "/customer/analytics-preference",
            schema: customerAnalyticsPreferenceSchema,
            signal: callOptions?.signal,
        }),
        setAnalyticsPreference: (input, commandOptions) => send({
            method: "POST",
            path: "/customer/analytics-preference",
            body: { optedOut: input.optedOut },
            schema: customerAnalyticsPreferenceSchema,
            idempotencyKey: commandOptions?.idempotencyKey,
            signal: commandOptions?.signal,
        }),
    };
}
